import {createSlice} from '@reduxjs/toolkit';
import type {PayloadAction} from '@reduxjs/toolkit';
import type {RootState} from '../store';
import {Media} from '../../types';
import {AxiosProgressEvent} from 'axios';

export interface UploadState {
  file?: File;
  media?: Media;
  modalVisible: boolean;
  uploadToCloud: boolean;
  shareToWT: boolean;
  encryption: boolean;
  persist: boolean;
  fileUploadProgress: AxiosProgressEvent | undefined;
  isUploadInProgress: boolean;
  totalChunks: number;
}

export interface UploadAction {
  file: File;
  media: Media;
}

export interface UploadContinueAction extends UploadAction {
  uploadId: string;
}

export interface UploadStopAction {
  uploadId: string;
}

const initialState: UploadState = {
  modalVisible: false,
  uploadToCloud: false,
  persist: true,
  shareToWT: false,
  encryption: false,
  fileUploadProgress: undefined,
  isUploadInProgress: false,
  totalChunks: 0,
};

export const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    startOnboarding: (state, action: PayloadAction<UploadAction>) => {
      console.info('start onboarding', action);
      state.file = action.payload.file;
      state.media = action.payload.media;
      state.modalVisible = true;
    },
    continue: (
      state,
      action: PayloadAction<UploadContinueAction>,
    ) => {},
    cancel: state => {
      state.modalVisible = false;
    },
    setMediaModalVisible: (state, action: PayloadAction<boolean>) => {
      state.modalVisible = action.payload;
      if (!action.payload) {
        state.file = undefined;
      }
    },
    setFileUploadProgress: (
      state,
      action: PayloadAction<AxiosProgressEvent>,
    ) => {
      state.fileUploadProgress = action.payload;
    },
    setUploadInProgress: (state, action: PayloadAction<boolean>) => {
      state.isUploadInProgress = action.payload;
    },
    setShareToWt: (state, action: PayloadAction<boolean>) => {
      state.shareToWT = action.payload;
    },
    setUploadToCloud: (state, action: PayloadAction<boolean>) => {
      state.uploadToCloud = action.payload;
    },
    setEncrypt: (state, action: PayloadAction<boolean>) => {
      state.encryption = action.payload;
    },
    setMetaForCloud: (state, action: PayloadAction<string>) => {
      state.media!.webpage_url = action.payload;
      state.media!.hasCloud = true;
      state.media!.extractor = 'cloud';
      state.media!.url = action.payload;
    },
  },
});

export const {
  startOnboarding,
  cancel,
  setMediaModalVisible,
  setFileUploadProgress,
  setUploadInProgress,
  setUploadToCloud,
  setEncrypt,
  setShareToWt,
  setMetaForCloud,
} = uploadSlice.actions;

export const selectCurrentMediaId = (state: RootState) =>
  state.media.currentMediaId;

export const selectIsLoadingMedia = (state: RootState) =>
  state.media.isLoadingMedia;

export const selectIsPlayingMedia = (state: RootState) =>
  state.media.isMediaPlaying;

export default uploadSlice.reducer;
